import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { ExecSessionRecord, ExecSessionStatus, ExecShellProfileId } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { createExecSession, keepaliveExecSession, openExecSocket, trackOpenExecSession, useExecCapability, type ExecSocket } from "@/api/execsession";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useI18n, useT } from "@/i18n/I18nProvider";
import { Notice, SessionPanel } from "@/features/drill/sessionPanel";
import { ExecRouteLeaveGuard, useBeforeUnloadGuard, useInDataRouter } from "./execExitGuard";
import { useExecWindowOwner } from "./execWindow";
import { DebugTerminalAlt } from "./DebugTerminalAlt";
import { DebugTerminalMine } from "./DebugTerminalMine";
import { ExecTranscriptView } from "./ExecTranscriptView";
import { ExecStatus, useExecStatusText, type ExecPhase } from "./DebugTerminalStatus";
import { mountTerminal, type TerminalHandle } from "./terminalView";

/**
 * Debug Terminal — ADR 0022 · ADR 0032 · Issue #10
 * --------------------------------------------------------------------------
 * 기본은 꺼짐입니다. 꺼진 배포·미지원 클러스터·권한 없음·교체된 Pod는 **서로 다른
 * 문구**로 끝납니다. 문구는 전부 서버가 소유합니다 — 브라우저 번들이 같은 문장을
 * 한 번 더 갖고 있으면 두 곳이 어긋나고, 화면이 서버가 모르는 이유를 지어냅니다.
 *
 * 열기 전에는 아무 요청도 보내지 않습니다. 세션은 위험 고지를 확인한 뒤 사용자가 눌러야
 * 열리고, 자동 재접속은 없습니다 — 자동 재시도는 재생성 Pod 오접속과 구분되지 않습니다.
 *
 * **키 입력은 해석하지 않고 바이트 그대로 보냅니다**(ADR 0032). Tab 자동완성·`clear`·
 * `↑` 히스토리·`Ctrl-C`는 전부 셸이 하는 일이고, 브라우저가 줄을 모아 보내는 동안에는
 * 그 바이트가 셸에 닿은 적이 없었습니다. 화면 제어는 에뮬레이터가 해석합니다.
 *
 * **세션이 끝나는 조건은 넷입니다**(ADR 0036): 셸의 `exit`, 세션 종료 버튼, 이 화면에서의
 * 이탈, 유휴 상한. 활동과 무관한 벽시계 상한은 없습니다 — 그래서 상한이 없는 배포에서는
 * 남은 시간을 세지 않습니다. 세는 척하면 화면이 오지 않을 종료를 카운트다운합니다.
 *
 * 같은 세션이 **별도 Window**에서도 열립니다(ADR 0036 결정 5). 자리는 하나이므로, 창이
 * 자리를 잡으면 이 패널은 세션을 놓고 그 사실만 말합니다.
 */

/**
 * 세션 종료 시각(RFC3339)을 타임스탬프로.
 *
 * 상한이 없는 배포에서는 **빈 문자열**이고(ADR 0036), `Date.parse("")`는 `NaN`입니다.
 * 그 값을 그대로 두면 남은 시간이 `NaN`초가 되어 화면이 있지도 않은 종료를 셉니다.
 */
export function parseDeadline(iso: string): number {
  const at = Date.parse(iso ?? "");
  return Number.isFinite(at) ? at : 0;
}

export function DebugTerminal(props: {
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  /**
   * 어디에 떠 있는 터미널인가.
   *
   *   inline  Pod 상세의 패널. 열기 버튼이 있고, 라우트 이탈을 확인 모달이 막습니다.
   *   window  별도 Window. 열자마자 연결할 수 있고, 닫기 버튼이 세션 종료를 확인합니다.
   */
  variant?: "inline" | "window";
  /** 별도 Window에서 닫기를 확인했을 때. 창을 닫는 일은 창이 압니다. */
  onWindowClose?: () => void;
}) {
  const { clusterId, namespace, podName, podUid, variant = "inline", onWindowClose } = props;
  const inWindow = variant === "window";
  /* 별도 Window는 터미널 하나를 보여 주려고 열린 창입니다 — 한 번 더 "열기"를 누르게
     하지 않습니다. 인라인 패널은 열기 전에 아무 요청도 보내지 않는 규칙 그대로입니다. */
  const [open, setOpen] = useState(inWindow);
  const [confirmed, setConfirmed] = useState(false);
  const [container, setContainer] = useState("");
  const [profile, setProfile] = useState<ExecShellProfileId>("sh");
  const [phase, setPhase] = useState<ExecPhase>("idle");
  /* 텍스트 대체의 재료입니다. 화면은 에뮬레이터가 그리고, 이 사본은 "얼마나 나왔는가"만
     세는 데 씁니다 — 요약이 원문을 옮기지는 않습니다(ADR 0025). */
  const [text, setText] = useState("");
  const [ended, setEnded] = useState<ExecSessionStatus>();
  const [error, setError] = useState("");
  const [left, setLeft] = useState(0);
  const [idle, setIdle] = useState(false);
  /* 스크린 리더 모드. 켜면 에뮬레이터가 버퍼를 ARIA live 영역으로 노출하고(ADR 0032),
     공용 Announcer의 요약도 자세해집니다. 끄면 live 영역이 없습니다. */
  const [srMode, setSrMode] = useState(false);
  /* 마지막으로 연 세션의 공개 식별자. 기록(ADR 0031)은 세션이 끝난 뒤 이 값으로 다시 봅니다. */
  const [sessionId, setSessionId] = useState("");
  const [showRecord, setShowRecord] = useState(false);
  const { t } = useT("terminal");
  /* 별도 Window에 실어 보낼 표시 언어입니다 — 새 창은 부모의 선택을 물려받지 않습니다. */
  const { locale } = useI18n();

  const sock = useRef<ExecSocket>();
  const activeAt = useRef(0);
  const deadline = useRef(0);
  const toggle = useRef<HTMLButtonElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const term = useRef<TerminalHandle>();

  const srModeRef = useRef(srMode);
  useEffect(() => {
    srModeRef.current = srMode;
    term.current?.setScreenReaderMode(srMode);
  }, [srMode]);

  const cap = useExecCapability(clusterId, namespace, podName, podUid, open);
  const limits = cap.data?.limits;
  const containers = cap.data?.containers ?? [];

  useEffect(() => {
    if (!container && containers.length) setContainer(containers.find((c) => c.selectable)?.name ?? containers[0]!.name);
  }, [container, containers]);

  /* 이 탭에서 열린 세션의 등록 해제. 로그아웃이 이 목록의 세션을 끝냅니다(ADR 0030 결정 4). */
  const untrack = useRef<() => void>();
  const stop = useCallback(() => {
    sock.current?.close();
    sock.current = undefined;
    untrack.current?.();
    untrack.current = undefined;
  }, []);
  useEffect(() => stop, [stop]);

  const close = useCallback(() => {
    stop();
    setPhase((p) => (p === "open" || p === "connecting" ? "closed" : p));
  }, [stop]);

  /* 에뮬레이터는 패널이 열려 있는 동안 **한 번만** 만듭니다. 연결마다 다시 만들면
     이전 세션의 스크롤백이 사라지고, 끝난 세션을 되읽을 수 없습니다. */
  useEffect(() => {
    const el = host.current;
    if (!el || term.current) return;
    /* 에뮬레이터는 늦게 실립니다. 그 사이에 패널이 닫히면 만들자마자 버립니다 —
       버리지 않으면 DOM에서 사라진 상자에 붙은 관찰자가 남습니다. */
    let cancelled = false;
    void mountTerminal(el, {
      onData: (data) => {
        const s = sock.current;
        if (!s?.ready) return;
        activeAt.current = Date.now();
        setIdle(false);
        s.send(data);
      },
      onResize: ({ cols, rows }) => sock.current?.resize({ width: cols, height: rows }),
      /* 터미널이 키를 전부 가져가므로 키보드 탈출구를 하나 남깁니다 — 셸이 쓰지 않는
         조합이어야 합니다. `Esc`는 이제 vi를 쓰는 사람의 것입니다. */
      onEscapeHatch: () => closeBtn.current?.focus(),
    }).then((handle) => {
      if (cancelled) {
        handle.dispose();
        return;
      }
      term.current = handle;
      handle.setScreenReaderMode(srModeRef.current);
    });
    return () => {
      cancelled = true;
      term.current?.dispose();
      term.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cap.data?.enabled]);

  /* 늦게 실리는 에뮬레이터가 준비됐을 때의 값이 필요합니다 — state를 effect 의존성으로
     넣으면 토글할 때마다 에뮬레이터를 다시 만들게 됩니다. */

  // 연결되면 초점을 터미널로 옮깁니다. 한 tick 미룹니다 — 연결 버튼이 사라지는 커밋에서
  // 브라우저가 초점을 body로 되돌리는 일이 effect보다 늦게 일어납니다.
  useEffect(() => {
    if (phase !== "open") return;
    const id = window.setTimeout(() => term.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [phase]);

  // 남은 시간·유휴 경고는 서버가 준 값을 **표시만** 합니다. 브라우저 시계로 끊지 않습니다.
  // 남은 시간의 기준은 ticket의 sessionExpiresAt이고, 상한이 없는 배포에서는 그 값이
  // 비어 있어 0입니다 — 그때 화면은 카운트다운을 띄우지 않습니다. (ADR 0032·0036)
  useEffect(() => {
    if (phase !== "open" || !limits) return;
    const timer = window.setInterval(() => {
      setLeft(deadline.current ? Math.max(0, Math.round((deadline.current - Date.now()) / 1000)) : 0);
      setIdle((Date.now() - activeAt.current) / 1000 >= limits.idleSeconds - limits.idleWarningSeconds);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [limits, phase]);

  /* 세션이 열려 있는 동안 인증 수명을 밀어 줍니다 (ADR 0032). 브라우저는 자기 토큰을
     이미 갱신하고 있고, 이 호출은 그 사실을 세션에 전할 뿐입니다 — 로그인해 있는 동안
     세션이 살고, 로그아웃하면 마지막으로 기록된 만료에서 끝납니다.
     서버를 폴링하는 것이 아니라 **열려 있는 세션 하나**에 대한 갱신입니다. */
  useEffect(() => {
    if (phase !== "open" || !sessionId) return;
    const push = () => {
      // 갱신된 종료 시각으로 남은 시간의 기준을 바꿉니다 — 발급 시점 값으로 계속 세면
      // 서버가 미뤄 준 종료를 화면이 여전히 카운트다운합니다.
      void keepaliveExecSession(sessionId)
        .then((res) => {
          deadline.current = parseDeadline(res.sessionExpiresAt);
        })
        .catch(() => undefined);
    };
    push();
    const timer = window.setInterval(push, 60_000);
    return () => window.clearInterval(timer);
  }, [phase, sessionId]);

  /* 세션이 끝나면 등록을 풀고 "내 세션" 요약을 다시 받습니다 — 끝난 사유·시각이 카드에 남습니다. */
  const refetchCap = cap.refetch;
  useEffect(() => {
    if (phase !== "closed" && phase !== "error") return;
    untrack.current?.();
    untrack.current = undefined;
    if (open) void refetchCap();
  }, [phase, open, refetchCap]);

  async function connect(target?: { container: string; profile: ExecShellProfileId }) {
    stop();
    term.current?.reset();
    setText("");
    setEnded(undefined);
    setError("");
    setIdle(false);
    setPhase("connecting");
    try {
      const ticket = await createExecSession(clusterId, namespace, podName, podUid, {
        container: target?.container ?? container,
        profile: target?.profile ?? profile,
        tty: true,
      });
      setSessionId(ticket.sessionId);
      untrack.current = trackOpenExecSession(ticket.sessionId);
      setShowRecord(false);
      deadline.current = parseDeadline(ticket.sessionExpiresAt);
      sock.current = openExecSocket(ticket.attachPath, {
        // **여기서만** 열림입니다. 그 전까지는 연결 중이고 입력은 셸에 닿지 않습니다.
        onOpen: () => {
          activeAt.current = Date.now();
          setPhase("open");
          // 열린 뒤 실제 크기를 한 번 알립니다. 그 전에는 보낼 소켓이 없었습니다.
          term.current?.reportSize();
        },
        onOutput: (chunk) => {
          activeAt.current = Date.now();
          term.current?.write(chunk);
          setText((prev) => (prev.length > 160_000 ? prev.slice(chunk.length) : prev) + chunk);
        },
        onStatus: (s) => {
          setEnded(s);
          setPhase("closed");
        },
        onClosed: (opened) => {
          // 승급이 거절되면 브라우저는 상태 코드도 본문도 볼 수 없습니다(스펙).
          // 서버 문구를 지어내지 않고 "열리지 않았다"는 사실만 말합니다.
          if (!opened) {
            setError(t("terminal.connectRejected"));
            setPhase((p) => (p === "closed" ? p : "error"));
            return;
          }
          setPhase((p) => (p === "error" ? p : "closed"));
        },
      });
    } catch (e) {
      setError(e instanceof HttpError ? e.body.message : t("terminal.openFailed"));
      setPhase("error");
    }
  }

  const denied = cap.error instanceof HttpError ? cap.error.body.message : "";
  const ready = cap.data?.enabled && cap.data.supported;
  const live = phase === "open";
  /* "끝내면 잃는 것이 있는" 상태입니다. 연결 중도 포함합니다 — 이탈로 소켓이 사라지면
     사용자는 왜 연결이 되다 말았는지 알 수 없습니다. */
  const busy = live || phase === "connecting";
  const statusText = useExecStatusText(phase, left);
  /* 남은 초가 빠진 단계 문구입니다 — 낭독은 이 값으로 합니다. 초가 든 문장을 그대로
     알리면 1초마다 새 문장이 되어 중복 접기가 무력해집니다. */
  const phaseText = useExecStatusText(phase, 0);

  /* 별도 Window 조율 (ADR 0036 결정 5). 창 안에서는 자리를 묻지 않습니다 — 자리를 잡은
     것이 자기 자신이고, 물으면 스스로에게 양보합니다. */
  const windowOwner = useExecWindowOwner(inWindow ? "" : podUid);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const claimedElsewhere = !inWindow && windowOwner.claimed;

  /* 자리는 하나입니다. 창이 잡으면 이 패널은 세션을 놓습니다 — 놓지 않으면 두 화면이
     같은 자리를 두고 다투고, 두 번째 연결은 서버의 동시성 거절을 받습니다. */
  useEffect(() => {
    if (claimedElsewhere) close();
  }, [claimedElsewhere, close]);

  /* 이탈 확인 (ADR 0036 결정 4). 새로고침·탭 닫기는 두 variant 모두 브라우저가 막고,
     라우트 이동은 인라인 패널에만 있습니다 — 별도 Window에는 이동할 라우트가 없습니다. */
  const inDataRouter = useInDataRouter();
  useBeforeUnloadGuard(busy);
  /** 별도 Window의 닫기 확인. 세션이 살아 있을 때만 묻습니다. */
  const [closing, setClosing] = useState(false);

  /* "같은 대상으로 다시 열기"(ADR 0030 결정 5). 확인 창이 위험 고지를 다시 보였으므로 확인을 켜고,
     지난 세션의 container·profile로 엽니다. 그 container가 지금 접속 불가면 서버가 거절 문장을 줍니다. */
  const reopen = (r: ExecSessionRecord) => {
    setConfirmed(true);
    setContainer(r.container);
    setProfile(r.profile);
    void connect({ container: r.container, profile: r.profile });
  };

  const body: ReactNode = (
    <div className="pad-5">
      {/* 자리를 창이 잡았다는 사실. 진입 실패도, 권한 없음도 아니므로 문구를 섞지 않습니다. */}
      {claimedElsewhere ? (
        <Notice role="status" testId="exec-window-claimed">
          <strong>{t("terminal.window.claimed")}</strong> {t("terminal.window.claimedHint")}
        </Notice>
      ) : null}

      {cap.data && !ready ? (
        <Notice role="status" testId="exec-unavailable">{cap.data.reason}</Notice>
      ) : null}

      {ready && !claimedElsewhere ? (
        <>
          <Notice tone="warning" role="note">
            <strong>{t("terminal.warning.lead")}</strong> {cap.data!.warning}
          </Notice>
          {/* 두 확인은 각자 한 줄입니다. 컨테이너 없이 `label`만 두면 inline이라
              한 줄에 붙고, 긴 문장이 체크박스 아래로 흘러 어느 문장이 어느 상자의
              것인지 알 수 없게 됩니다. */}
          <div className="exec-gate">
            <label className="exec-gate__item">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.currentTarget.checked)}
                data-testid="exec-risk-confirm"
              />
              <span>{t("terminal.risk.confirm")}</span>
            </label>
            <label className="exec-gate__item">
              <input
                type="checkbox"
                checked={srMode}
                onChange={(e) => setSrMode(e.currentTarget.checked)}
                data-testid="exec-sr-mode"
              />
              <span>
                {t("terminal.srMode.label")} <span className="muted">{t("terminal.srMode.hint")}</span>
              </span>
            </label>
          </div>
          <div className="exec-toolbar">
            <label>
              {t("terminal.container")}{" "}
              <select value={container} onChange={(e) => setContainer(e.currentTarget.value)} disabled={live} aria-label={t("terminal.container.aria")}>
                {/* container 이름·state는 Kubernetes 값입니다 — 번역하지 않습니다. */}
                {containers.map((c) => (
                  <option key={c.name} value={c.name} disabled={!c.selectable}>
                    {c.selectable ? c.name : t("terminal.container.unselectable", { name: c.name, state: c.state })}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("terminal.shell")}{" "}
              <select value={profile} onChange={(e) => setProfile(e.currentTarget.value as ExecShellProfileId)} disabled={live} aria-label={t("terminal.shell.aria")}>
                {cap.data!.profiles.map((p) => (
                  <option key={p.id} value={p.id} title={p.description}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {live ? (
              <button type="button" className="btn" onClick={close} data-testid="exec-close">
                {t("terminal.disconnect")}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void connect()}
                disabled={!confirmed || !containers.some((c) => c.selectable)}
                data-testid="exec-connect"
              >
                {t("terminal.connect")}
              </button>
            )}
            {/* 별도 창으로 자리를 옮깁니다. **셸 상태는 따라가지 않습니다** — 소켓은 창을
                넘지 못하므로 여기서 끝내고 저기서 새로 엽니다(ADR 0036 대안 D). */}
            {!inWindow ? (
              <button
                type="button"
                className="chip"
                data-testid="exec-window-open"
                title={t("terminal.window.openHint")}
                /* 창이 실제로 열렸을 때만 자리를 넘깁니다. 먼저 끊고 나서 팝업이 막히면
                   사용자는 아무것도 하지 않았는데 세션만 잃습니다 — 세션을 끝내는 것은
                   창이 자리를 잡았다는 신호이고, 그 처리는 위의 effect 하나가 합니다. */
                onClick={() => setPopupBlocked(!windowOwner.open({ namespace, podName, locale }))}
              >
                {t("terminal.window.open")}
              </button>
            ) : null}
            {limits ? (
              <span className="muted">
                {t("terminal.limits", { idle: limits.idleSeconds, sessions: limits.maxSessionsPerSubject })}
              </span>
            ) : null}
          </div>
          {/* 팝업 차단은 오류가 아니라 브라우저 설정입니다. 이 화면은 그대로 쓸 수 있습니다. */}
          {popupBlocked ? (
            <Notice tone="warning" role="alert" testId="exec-window-blocked">{t("terminal.window.blocked")}</Notice>
          ) : null}
          <DebugTerminalMine
            mine={cap.data!.mySessions}
            podUid={podUid}
            currentSessionId={busy ? sessionId : ""}
            busy={busy}
            warning={cap.data!.warning}
            onReopen={reopen}
          />
          <ExecStatus text={statusText} live={live} idleWarning={idle} error={error} ended={ended} />
          {/* 텍스트 대체는 언제나 보입니다. 낭독은 공용 Announcer 하나가 합니다. */}
          <DebugTerminalAlt text={text} status={statusText} phase={phaseText} verbose={srMode} />
          {/* 에뮬레이터가 그리는 화면입니다(ADR 0032). 키 입력은 해석하지 않고 바이트
              그대로 셸에 갑니다 — Tab 자동완성·clear·히스토리는 셸이 합니다.
              에뮬레이터가 늦게 실리는 동안에도 이름이 유효하도록 역할을 둡니다. (#183) */}
          <div className="exec-screen" ref={host} role="group" aria-label={t("terminal.transcript.label")} data-testid="exec-transcript" />
          <p className="muted">{t("terminal.escapeHint")}</p>
          {/* 세션 기록(ADR 0031). 끝난 세션의 **서버가 마스킹해 저장한 출력**을 다시 봅니다. */}
          {cap.data!.transcript.enabled && sessionId && phase === "closed" ? (
            <div className="exec-toolbar">
              <button type="button" className="chip" onClick={() => setShowRecord((v) => !v)} data-testid="exec-record-toggle">
                {showRecord ? t("terminal.record.hide") : t("terminal.record.show")}
              </button>
            </div>
          ) : null}
          {showRecord && sessionId ? <ExecTranscriptView sessionId={sessionId} /> : null}
        </>
      ) : null}
    </div>
  );

  /* 라우트 이탈 확인. data router 밖(단위 테스트의 MemoryRouter)에서는 `useBlocker`가
     던지므로 마운트하지 않습니다 — 조건은 여기서 판정하고 훅은 그 안에서 항상 불립니다. */
  const leaveGuard = !inWindow && inDataRouter ? <ExecRouteLeaveGuard active={busy} onLeave={stop} /> : null;

  if (inWindow) {
    return (
      <div className="exec-window">
        <header className="exec-window__bar">
          <div className="exec-window__ident">
            <h1 className="exec-window__title">{t("terminal.title")}</h1>
            {/* Kubernetes 값입니다 — 번역하지 않습니다. */}
            <p className="exec-window__meta muted">
              {namespace} / {podName}
            </p>
          </div>
          <button
            ref={closeBtn}
            type="button"
            className="chip"
            data-testid="exec-window-close"
            onClick={() => (busy ? setClosing(true) : onWindowClose?.())}
          >
            {t("terminal.window.close")}
          </button>
        </header>
        {cap.isLoading ? <Notice role="status">{t("terminal.loading")}</Notice> : null}
        {denied ? (
          <Notice tone="warning" role="alert" testId="exec-denied">{denied}</Notice>
        ) : null}
        {body}
        <ConfirmDialog
          open={closing}
          title={t("terminal.confirm.close.title")}
          message={t("terminal.confirm.close.body")}
          confirmLabel={t("terminal.confirm.close.ok")}
          cancelLabel={t("terminal.confirm.stay")}
          testId="exec-window-close-confirm"
          onConfirm={() => {
            setClosing(false);
            close();
            onWindowClose?.();
          }}
          onCancel={() => setClosing(false)}
        />
      </div>
    );
  }

  return (
    <>
      <SessionPanel
        title={t("terminal.title")}
        subtitle={t("terminal.subtitle")}
        open={open}
        onOpen={() => setOpen(true)}
        openLabel={t("terminal.open")}
        openTestId="exec-open"
        openRef={toggle}
        openHint={t("terminal.openHint")}
        loading={cap.isLoading}
        loadingLabel={t("terminal.loading")}
        error={denied || undefined}
        errorTestId="exec-denied"
        footer={
          <div className="pad-5">
            <button
              ref={closeBtn}
              type="button"
              className="chip"
              data-testid="exec-panel-close"
              onClick={() => {
                close();
                setOpen(false);
                window.setTimeout(() => toggle.current?.focus(), 0);
              }}
            >
              {t("terminal.panelClose")}
            </button>
          </div>
        }
      >
        {body}
      </SessionPanel>
      {leaveGuard}
    </>
  );
}
